import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { fetchHistoricalPrices } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './HistoricalPriceChart.module.css';

const HistoricalPriceChart = ({
  listing,
  currency = 'USD',
  className,
  showConfidenceInterval = false,
}) => {
  const intl = useIntl();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [timeRange, setTimeRange] = useState('5y');
  const chartRef = useRef(null);

  // Generate fallback price data based on current listing price and category
  const generateFallbackData = (currentPrice, category, brand, selectedTimeRange = '5y') => {
    if (!currentPrice) return null;

    const basePrice = currentPrice.amount / 100; // Convert from minor units
    const dataPoints = [];

    // Determine number of months based on time range
    const timeRangeMonths = {
      '1y': 12,
      '3y': 36,
      '5y': 60,
    };
    const months = timeRangeMonths[selectedTimeRange] || 60;

    // Generate data for the selected time range
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);

      // Create realistic price variations based on category
      let variation = 0.1; // Default 10% variation

      // Adjust variation by category
      if (category?.toLowerCase().includes('electronics')) {
        variation = 0.15; // Electronics depreciate more
      } else if (
        category?.toLowerCase().includes('collectible') ||
        category?.toLowerCase().includes('art')
      ) {
        variation = 0.25; // Collectibles have higher variation
      } else if (
        category?.toLowerCase().includes('vehicle') ||
        category?.toLowerCase().includes('car')
      ) {
        variation = 0.12; // Vehicles have moderate variation
      }

      // Create trend - slight depreciation for most items
      const ageFactor = i / months; // 0 to 1, where 1 is oldest
      const trendAdjustment = category?.toLowerCase().includes('collectible')
        ? 1 + ageFactor * 0.1 // Collectibles might appreciate
        : 1 - ageFactor * 0.05; // Most items depreciate slightly

      // Add some randomness
      const randomFactor = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
      const variationFactor = 1 + (Math.random() - 0.5) * variation;

      const estimatedPrice = basePrice * trendAdjustment * variationFactor * randomFactor;

      dataPoints.push({
        date: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
        price: Math.max(estimatedPrice, basePrice * 0.3), // Don't go below 30% of current price
        estimated: true,
      });
    }

    const prices = dataPoints.map(point => point.price);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);

    return {
      average_price: avgPrice,
      highest_price: maxPrice,
      lowest_price: minPrice,
      data_points: dataPoints,
      source: 'estimated',
      generated_at: new Date().toISOString(),
      note: `Price estimates based on current listing price of ${formatCurrency(
        basePrice
      )} and ${category ||
        'general'} market trends. Actual historical prices may vary significantly.`,
    };
  };

  useEffect(() => {
    if (!listing) return;

    const { title = '', publicData = {}, description } = listing.attributes;
    const { category, subcategory, condition, brand, model } = publicData;

    if (!title) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);

        // If we're already in fallback mode and only timeRange changed,
        // just regenerate fallback data without trying API again
        if (fallbackMode) {
          tryFallback();
          return;
        }

        setFallbackMode(false);

        const response = await fetchHistoricalPrices({
          title,
          category,
          subcategory,
          condition,
          brand,
          model,
          description,
        });

        if (isCancelled) return;

        if (response.data_points && response.data_points.length > 0) {
          // Filter data based on selected time range
          const timeRangeMonths = {
            '1y': 12,
            '3y': 36,
            '5y': 60,
          };
          const monthsToShow = timeRangeMonths[timeRange] || 60;

          // Sort data points by date and take the most recent ones
          const sortedData = response.data_points
            .sort((a, b) => new Date(b.date + '-01') - new Date(a.date + '-01'))
            .slice(0, monthsToShow)
            .sort((a, b) => new Date(a.date + '-01') - new Date(b.date + '-01'));

          // Calculate confidence interval if enabled
          const prices = sortedData.map(point => point.price);
          const mean = prices.reduce((sum, price) => sum + price, 0) / prices.length;
          const variance =
            prices.reduce((sum, price) => sum + Math.pow(price - mean, 2), 0) / prices.length;
          const stdDev = Math.sqrt(variance);
          const confidenceInterval = 1.96 * stdDev; // 95% confidence interval

          // Transform Grok API response to chart format
          const formattedData = sortedData.map(item => ({
            date: new Date(item.date + '-01').toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
            }),
            price: item.price,
            // Add confidence interval bands
            upperBound: showConfidenceInterval ? item.price + confidenceInterval * 0.5 : null,
            lowerBound: showConfidenceInterval
              ? Math.max(0, item.price - confidenceInterval * 0.5)
              : null,
            // Add the highest, average, lowest for reference lines
            highest: response.highest_price,
            average: response.average_price,
            lowest: response.lowest_price,
          }));

          // Create summary data for overall statistics display
          const summaryData = {
            average_price: mean,
            highest_price: Math.max(...prices),
            lowest_price: Math.min(...prices),
            confidence_interval: confidenceInterval,
            data_points: formattedData,
            source: response.source,
            generated_at: response.generated_at,
          };

          setData(summaryData);
          setError(null);
        } else {
          // No data from API, fall back to estimation
          tryFallback();
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Error fetching historical prices:', err);
          // Try fallback instead of just showing error
          tryFallback();
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    const tryFallback = () => {
      if (isCancelled) return;

      const currentPrice = listing?.attributes?.price;
      if (currentPrice) {
        console.log('Using fallback price estimation based on current listing price');
        const fallbackData = generateFallbackData(currentPrice, category, brand, timeRange);
        if (fallbackData) {
          setData(fallbackData);
          setFallbackMode(true);
          setError(null);
          return;
        }
      }

      // Last resort: set error state
      setError(new Error('Unable to load or estimate historical prices'));
      setData(null);
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [listing, timeRange]);

  const formatCurrency = value => {
    if (!value) return '';
    const money = new sdkTypes.Money(Math.round(value * 100), currency);
    return formatMoney(intl, money);
  };

  const renderSimpleChart = dataPoints => {
    if (!dataPoints || dataPoints.length === 0) return null;

    const width = 800;
    const height = 300;
    const padding = 60;
    const chartWidth = width - 2 * padding;
    const chartHeight = height - 2 * padding;

    // Calculate scales
    const prices = dataPoints.map(d => d.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;

    // Create monotone cubic interpolation for financial chart smoothness
    const createMonotonePath = points => {
      if (points.length < 2) return '';

      // Calculate slopes for monotone interpolation
      const slopes = [];
      const deltas = [];

      // Calculate deltas
      for (let i = 0; i < points.length - 1; i++) {
        deltas[i] = (points[i + 1].y - points[i].y) / (points[i + 1].x - points[i].x);
      }

      // Calculate slopes using monotone cubic interpolation
      slopes[0] = deltas[0];
      for (let i = 1; i < points.length - 1; i++) {
        if (deltas[i - 1] * deltas[i] <= 0) {
          slopes[i] = 0;
        } else {
          slopes[i] = (deltas[i - 1] + deltas[i]) / 2;
        }
      }
      slopes[points.length - 1] = deltas[deltas.length - 1];

      // Build the smooth path
      let path = `M ${points[0].x} ${points[0].y}`;

      for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const dx = p1.x - p0.x;

        // Control points for cubic bezier
        const cp1x = p0.x + dx / 3;
        const cp1y = p0.y + (slopes[i - 1] * dx) / 3;
        const cp2x = p1.x - dx / 3;
        const cp2y = p1.y - (slopes[i] * dx) / 3;

        path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p1.x} ${p1.y}`;
      }

      return path;
    };

    // Calculate points for the chart
    const chartPoints = dataPoints.map((point, index) => ({
      x: padding + (index * chartWidth) / (dataPoints.length - 1),
      y: padding + chartHeight - ((point.price - minPrice) / priceRange) * chartHeight,
      price: point.price,
    }));

    const pathData = createMonotonePath(chartPoints);

    // Calculate dynamic confidence band based on local variance
    const calculateLocalVariance = (points, index, windowSize = 5) => {
      const start = Math.max(0, index - Math.floor(windowSize / 2));
      const end = Math.min(points.length, index + Math.floor(windowSize / 2) + 1);
      const localPoints = points.slice(start, end);

      if (localPoints.length < 2) return priceRange * 0.08;

      const localPrices = localPoints.map(p => p.price);
      const localMean = localPrices.reduce((sum, price) => sum + price, 0) / localPrices.length;
      const localVariance =
        localPrices.reduce((sum, price) => sum + Math.pow(price - localMean, 2), 0) /
        localPrices.length;
      const localStdDev = Math.sqrt(localVariance);

      // Use local standard deviation but with min/max bounds for visual consistency
      const normalizedStdDev = Math.max(
        priceRange * 0.05,
        Math.min(priceRange * 0.15, localStdDev * 1.5)
      );
      return normalizedStdDev;
    };

    const upperBandPoints = chartPoints.map((point, index) => {
      const localVariance = calculateLocalVariance(chartPoints, index);
      const variancePixels = (localVariance / priceRange) * chartHeight;
      return {
        x: point.x,
        y: Math.max(padding, point.y - variancePixels),
        price: point.price,
      };
    });

    const lowerBandPoints = chartPoints.map((point, index) => {
      const localVariance = calculateLocalVariance(chartPoints, index);
      const variancePixels = (localVariance / priceRange) * chartHeight;
      return {
        x: point.x,
        y: Math.min(padding + chartHeight, point.y + variancePixels),
        price: point.price,
      };
    });

    const upperBandPath = createMonotonePath(upperBandPoints);
    const lowerBandPath = createMonotonePath(lowerBandPoints);

    // Create confidence band path (upper line + reverse lower line)
    const confidenceBandPath =
      upperBandPath +
      ` L ${lowerBandPoints[lowerBandPoints.length - 1].x} ${
        lowerBandPoints[lowerBandPoints.length - 1].y
      }` +
      lowerBandPoints
        .slice()
        .reverse()
        .slice(1)
        .map(point => ` L ${point.x} ${point.y}`)
        .join('') +
      ' Z';

    // Y-axis labels
    const yLabels = [];
    for (let i = 0; i <= 4; i++) {
      const price = minPrice + (priceRange * i) / 4;
      const y = padding + chartHeight - (i * chartHeight) / 4;
      yLabels.push({ price, y });
    }

    // X-axis labels (every 12 months)
    const xLabels = dataPoints
      .filter((_, index) => index % 12 === 0)
      .map((point, index) => {
        const x = padding + (index * 12 * chartWidth) / (dataPoints.length - 1);
        return { date: point.date, x };
      });

    return (
      <div className={css.chartContainer}>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Grid lines */}
          {yLabels.map((label, index) => (
            <line
              key={`y-grid-${index}`}
              x1={padding}
              y1={label.y}
              x2={padding + chartWidth}
              y2={label.y}
              stroke="#e1e5e9"
              strokeDasharray="3 3"
            />
          ))}

          {/* Y-axis */}
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={padding + chartHeight}
            stroke="#2c3e50"
            strokeWidth="1"
          />

          {/* X-axis */}
          <line
            x1={padding}
            y1={padding + chartHeight}
            x2={padding + chartWidth}
            y2={padding + chartHeight}
            stroke="#2c3e50"
            strokeWidth="1"
          />

          {/* Confidence band around the line */}
          <path d={confidenceBandPath} fill="#6E712E" fillOpacity="0.12" stroke="none" />

          {/* Price line */}
          <path
            d={pathData}
            fill="none"
            stroke="#6E712E"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Y-axis labels */}
          {yLabels.map((label, index) => (
            <text
              key={`y-label-${index}`}
              x={padding - 10}
              y={label.y + 5}
              textAnchor="end"
              fontSize="12"
              fill="#7f8c8d"
            >
              {formatCurrency(label.price)}
            </text>
          ))}

          {/* X-axis labels */}
          {xLabels.map((label, index) => (
            <text
              key={`x-label-${index}`}
              x={label.x}
              y={padding + chartHeight + 20}
              textAnchor="middle"
              fontSize="12"
              fill="#7f8c8d"
            >
              {label.date}
            </text>
          ))}
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`${css.container} ${className}`}>
        <div className={css.loading}>
          <FormattedMessage id="HistoricalPriceChart.loading" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${css.container} ${className}`}>
        <div className={css.error}>
          <FormattedMessage id="HistoricalPriceChart.error" />
        </div>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className={`${css.container} ${className}`}>
        <div className={css.noData}>
          <FormattedMessage id="HistoricalPriceChart.noData" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${css.container} ${className}`}>
      <div className={css.header}>
        <div className={css.titleSection}>
          <h3 className={css.title}>
            <FormattedMessage id="HistoricalPriceChart.title" />
          </h3>
          <p className={css.subtitle}>
            <FormattedMessage
              id="HistoricalPriceChart.subtitle"
              values={{ category: listing.attributes.publicData?.category || 'Similar items' }}
            />
          </p>
        </div>

        <div className={css.controls}>
          <div className={css.controlGroup}>
            <label className={css.controlLabel}>
              <FormattedMessage id="HistoricalPriceChart.timeRange" />:
            </label>
            <select
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
              className={css.controlSelect}
            >
              <option value="1y">1 Year</option>
              <option value="3y">3 Years</option>
              <option value="5y">5 Years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className={css.summaryStats}>
        <div className={css.statItem}>
          <span className={css.statLabel}>
            <FormattedMessage id="HistoricalPriceChart.average" />:
          </span>
          <span className={css.statValue}>{formatCurrency(data.average_price)}</span>
        </div>
        <div className={css.statItem}>
          <span className={css.statLabel}>
            <FormattedMessage id="HistoricalPriceChart.highest" />:
          </span>
          <span className={css.statValue}>{formatCurrency(data.highest_price)}</span>
        </div>
        <div className={css.statItem}>
          <span className={css.statLabel}>
            <FormattedMessage id="HistoricalPriceChart.lowest" />:
          </span>
          <span className={css.statValue}>{formatCurrency(data.lowest_price)}</span>
        </div>
      </div>

      {renderSimpleChart(data.data_points)}

      <div className={css.sourceInfo}>
        <p className={css.sourceText}>
          {data.source === 'mock_data' ? (
            <FormattedMessage id="HistoricalPriceChart.mockDataNotice" />
          ) : data.source === 'estimated' ? (
            <FormattedMessage id="HistoricalPriceChart.estimatedDataNotice" />
          ) : (
            <FormattedMessage id="HistoricalPriceChart.poweredByGrok" />
          )}
        </p>
        {fallbackMode && (
          <div className={css.fallbackWarning}>
            <p className={css.warningText}>
              <FormattedMessage id="HistoricalPriceChart.fallbackWarning" />
            </p>
          </div>
        )}
        {data.generated_at && (
          <p className={css.generatedAt}>
            <FormattedMessage
              id="HistoricalPriceChart.generatedAt"
              values={{
                date: new Date(data.generated_at).toLocaleDateString(),
              }}
            />
          </p>
        )}
        {data.note && <p className={css.note}>{data.note}</p>}
      </div>
    </div>
  );
};

export default HistoricalPriceChart;
